import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getClients, deleteClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Eye, Edit, Trash2, Building2, User, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import SmartSearch from '@/components/SmartSearch';

const emirates = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'UAQ', 'Ras Al Khaimah', 'Fujairah'];

// Column definitions for smart search
const searchColumns = [
  { key: 'client_code', label: 'Code', type: 'text' },
  { key: 'display_name', label: 'Name', type: 'text' },
  { key: 'client_type', label: 'Type', type: 'select', options: [
    { value: 'Company', label: 'Company' },
    { value: 'Individual', label: 'Individual' }
  ]},
  { key: 'emirate', label: 'Emirate', type: 'select', options: emirates.map(e => ({ value: e, label: e })) },
  { key: 'contact_person', label: 'Contact Person', type: 'text' },
  { key: 'contact_phone', label: 'Phone', type: 'text' },
  { key: 'contact_email', label: 'Email', type: 'text' },
  { key: 'outstanding_balance', label: 'Outstanding', type: 'number' },
  { key: 'wallet_balance', label: 'Wallet Balance', type: 'number' },
  { key: 'status', label: 'Status', type: 'select', options: [
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' }
  ]},
  { key: 'trn', label: 'TRN', type: 'text' },
  { key: 'license_no', label: 'License No', type: 'text' },
];

// Quick filter presets
const quickFilters = [
  { label: 'Companies Only', filters: { client_type: 'Company' } },
  { label: 'Individuals Only', filters: { client_type: 'Individual' } },
  { label: 'Dubai', filters: { emirate: 'Dubai' } },
  { label: 'Abu Dhabi', filters: { emirate: 'Abu Dhabi' } },
  { label: 'Has Outstanding', filters: { outstanding_balance: '>0' } },
  { label: 'Active', filters: { status: 'Active' } },
];

export default function ClientsPage() {
  const navigate = useNavigate();
  const [allClients, setAllClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const res = await getClients({ limit: 1000 });
      setAllClients(res.data.items);
      setFilteredClients(res.data.items);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!clientToDelete) return;
    try {
      await deleteClient(clientToDelete.id);
      toast.success('Client deleted');
      loadClients();
    } catch (error) {
      toast.error('Failed to delete client');
    } finally {
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Clients</h1>
          <p className="text-muted-foreground">Manage companies and individual clients</p>
        </div>
        <Button onClick={() => navigate('/clients/new')} data-testid="new-client-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <SmartSearch
            data={allClients}
            columns={searchColumns}
            onFilter={setFilteredClients}
            quickFilters={quickFilters}
            placeholder="Search by name, code, phone, email, TRN..."
          />
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Emirate</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-right">Wallet</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow key={client.id} data-testid={`client-row-${client.id}`}>
                    <TableCell className="font-mono text-xs">{client.client_code}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {client.client_type === 'Company' ? (
                          <Building2 className="w-4 h-4 text-primary" />
                        ) : (
                          <User className="w-4 h-4 text-muted-foreground" />
                        )}
                        <Link 
                          to={`/clients/${client.id}`} 
                          className="font-medium hover:text-primary"
                        >
                          {client.display_name}
                        </Link>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{client.client_type}</Badge>
                    </TableCell>
                    <TableCell>{client.emirate}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{client.contact_person}</p>
                        <p className="text-xs text-muted-foreground">{client.contact_phone}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      <span className={client.outstanding_balance > 0 ? 'text-red-500' : ''}>
                        {formatCurrency(client.outstanding_balance)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Wallet className="w-3 h-3 text-muted-foreground" />
                        <span className="font-mono text-sm">{formatCurrency(client.wallet_balance)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={client.status === 'Active' ? 'default' : 'secondary'}>
                        {client.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/clients/${client.id}`)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/clients/${client.id}/edit`)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => { setClientToDelete(client); setDeleteDialogOpen(true); }}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredClients.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No clients found matching your search criteria
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {filteredClients.length} of {allClients.length} clients
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{clientToDelete?.display_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setClientToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
